import { Link } from 'react-router';
import { COMPANY } from '@/content/company';
import { Clause, LegalPage } from './LegalPage';

const TOC = [
  '1. About these terms',
  '2. Site operator',
  '3. Use of the site',
  '4. Content & IP',
  '5. Enquiries',
  '6. Fees & invoicing',
  '7. No warranty',
  '8. Liability',
  '9. External links',
  '10. Governing law',
  '11. General',
] as const;

const mail = <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>;

export function Terms() {
  return (
    <LegalPage
      eyebrow="Using this website"
      title="Terms of Use"
      toc={TOC}
      intro={
        <>
          These terms govern your use of skabro.eu. By using the website you accept them. They do
          not govern the delivery of a project - that is covered by a separate written agreement,
          alongside our{' '}
          <Link to="/payment-and-refunds">Payment, Refund &amp; Cancellation Policy</Link>.
        </>
      }
    >
      <Clause n={1} title="About these terms">
        <p>
          This website is informational. Nothing published here is a binding offer, a quotation or
          professional advice for a specific situation. Any engagement between you and Skabro begins
          with a signed contract or written order confirmation. We contract with businesses and
          organisations rather than with consumers.
        </p>
        {/*
         * The B2B carve-out above is what makes the no-withdrawal position in
         * the Payment policy hold, so it is worth stating what happens if it
         * ever fails to apply. Mandatory consumer law applies whether or not a
         * term says so; a document that pretends otherwise invites a court to
         * strike the surrounding clause rather than just that sentence. Saying
         * it plainly costs nothing and loses nothing.
         */}
        <p>
          If we do at any point contract with you as a consumer, the protections Slovak and EU
          consumer law give you apply in full, whatever these terms or the policies they refer to
          say, and the mandatory rules prevail over anything inconsistent with them.
        </p>
      </Clause>

      <Clause n={2} title="Site operator">
        <p>
          The site is operated by {COMPANY.legalName}, {COMPANY.address.street},{' '}
          {COMPANY.address.localityFormal}, {COMPANY.address.country}. IČO {COMPANY.ico}, DIČ{' '}
          {COMPANY.dic}, IČ DPH {COMPANY.icDph}. Registered in the Commercial Register of{' '}
          {COMPANY.register.court}, {COMPANY.register.sectionEn}. Statutory body:{' '}
          {COMPANY.statutory.name}, {COMPANY.statutory.role}, acting independently for the company.
          {/*
           * Email only. § 3a of the Commercial Code and the § 4(1) list in Act
           * 22/2004 Coll. require the business name, seat, register entry and a
           * means of rapid electronic contact - which the email address is. A
           * telephone number is not on either list, so withholding it from this
           * clause concedes nothing, and the number is reachable in one click from
           * the contact page in any case.
           */}
          Contact: {mail}, or by telephone - the number is on our{' '}
          <Link to="/contact">contact page</Link>.
        </p>
        {/* § 4(1) of Act 22/2004 Coll. requires the supervisory authority to be
            published alongside the identification data above. It is the one item
            on that list the site used to omit. */}
        <p>
          <strong>Supervisory authority.</strong> Compliance with Slovak Act No. 22/2004 Coll. on
          electronic commerce is supervised by the {COMPANY.supervision.authority} (
          {COMPANY.supervision.authorityEn}), {COMPANY.supervision.office},{' '}
          {COMPANY.supervision.address},{' '}
          <a href={`mailto:${COMPANY.supervision.email}`}>{COMPANY.supervision.email}</a>,{' '}
          <a href={COMPANY.supervision.web} target="_blank" rel="noopener noreferrer">
            soi.sk
          </a>
          . Personal data matters fall to the {COMPANY.dataProtectionAuthority.name},{' '}
          {COMPANY.dataProtectionAuthority.address} - see our{' '}
          <Link to="/privacy">Privacy Policy</Link>.
        </p>
      </Clause>

      <Clause n={3} title="Use of the site">
        <p>
          You may use the site for lawful purposes only. You agree not to attempt to gain
          unauthorised access to it, to disrupt its operation, to submit malicious or misleading
          content through the contact form, or to collect data from the site by automated means.
        </p>
      </Clause>

      <Clause n={4} title="Content and intellectual property">
        <p>
          All text, design, code and graphics on this site are owned by {COMPANY.legalName} or used
          with permission, and are protected by copyright. You may view and print pages for your own
          information. Reproducing, republishing or reusing content commercially requires our prior
          written consent. Third-party names and trademarks mentioned on this site remain the
          property of their owners and are used only to describe technologies we work with.
        </p>
      </Clause>

      <Clause n={5} title="Enquiries sent through the site">
        <p>
          Messages you send us are treated as confidential business correspondence and are used only
          to respond to your enquiry, as described in our <Link to="/privacy">Privacy Policy</Link>.
          Please do not send confidential material or credentials before a confidentiality agreement
          is in place.
        </p>
      </Clause>

      <Clause n={6} title="Fees, invoicing and cancellation">
        <p>
          Work is quoted individually and invoiced in EUR. Unless a signed agreement states
          otherwise, invoices are payable within 14 days of issue; VAT is applied under Slovak and
          EU rules, with reverse charge for VAT-registered clients in other member states.
        </p>
        <p>
          Pricing, invoicing, delivery, cancellation and refunds are set out in full in our{' '}
          <Link to="/payment-and-refunds">Payment, Refund &amp; Cancellation Policy</Link>, which
          forms part of these terms. Please raise any billing question with us directly at {mail}{' '}
          before starting a chargeback or dispute with a payment provider - we aim to answer billing
          enquiries within one working day.
        </p>
      </Clause>

      <Clause n={7} title="Accuracy and availability">
        <p>
          We keep the information on this site current and accurate to the best of our knowledge,
          but it is provided without warranty of completeness. We may change or remove content, and
          we do not guarantee uninterrupted availability of the site.
        </p>
      </Clause>

      <Clause n={8} title="Limitation of liability">
        <p>
          To the extent permitted by law, {COMPANY.legalName} is not liable for indirect or
          consequential damage arising from use of this website. Nothing in these terms limits
          liability that cannot be limited under Slovak law.
        </p>
      </Clause>

      <Clause n={9} title="External links">
        <p>
          Where we link to third-party websites, we do so for convenience. We are not responsible
          for their content or their handling of your data.
        </p>
      </Clause>

      <Clause n={10} title="Governing law and changes">
        <p>
          These terms are governed by the law of the Slovak Republic, and disputes fall under the
          jurisdiction of the competent Slovak courts. We may update these terms; the version
          published on this page at the time of your visit applies.
        </p>
        {/* No EU ODR link on purpose. The European Commission's Online Dispute
            Resolution platform was shut down on 20 July 2025, so the link every
            older template still carries now points at nothing. It is also a
            consumer mechanism, and clause 1 establishes that we contract
            business-to-business. Do not let a template put it back. */}
      </Clause>

      <Clause n={11} title="General">
        <p>
          If any provision of these terms is found to be invalid or unenforceable, the rest remain
          in force, and the invalid provision is treated as replaced by the closest enforceable
          equivalent.
        </p>
        <p>
          You may not assign or transfer your rights under these terms without our written consent.
          We may assign ours to a successor in connection with a reorganisation or transfer of the
          business, provided your rights are not reduced by it.
        </p>
        <p>
          Failing to enforce a provision on one occasion does not waive it. These terms, together
          with the policies they refer to, are the whole agreement between us in respect of your use
          of this website.
        </p>
      </Clause>
    </LegalPage>
  );
}
